import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, viewChildren } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/login/login.state';
import { DoTanLoginAction } from '../../store/login/login.action';
import { InvalidDirective } from '../../../../forms/directives/invalid.directive';
import { Observable, Subscription } from 'rxjs';
import { BrandingService } from '../../../branding/providers/branding.service';
import { BrandingState } from '../../../branding/store/branding.state';
import { LEGAL_DATA_PROCESS_TERMS, LEGAL_DATA_SECURITY, LEGAL_TERMS, LOGIN_ROUTE } from '../../../../routes';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { AccessibilityService } from '../../../../providers/accessibility/accessibility.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { LoginLayoutComponent } from '../../components/login-layout/login-layout.component';
import { NgStyle, NgClass, AsyncPipe } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap/tooltip';
import { BrowserInfoComponent } from '../../components/browser-info/browser-info.component';

@Component({
    selector: 'app-tan-login',
    templateUrl: './tan-login.component.html',
    styleUrls: ['./tan-login.component.scss'],
    imports: [LoginLayoutComponent, NgStyle, FormsModule, ReactiveFormsModule, InvalidDirective, NgbTooltip, RouterLink, BrowserInfoComponent, NgClass, AsyncPipe, TranslatePipe]
})
export class TanLoginComponent implements OnInit, OnDestroy {
    tanLoginForm: UntypedFormGroup;
    doctorLoginRoute = LOGIN_ROUTE;
    dataSecurityRoute = LEGAL_DATA_SECURITY;
    termsRoute = LEGAL_TERMS;
    dataProcessTermsRoute = LEGAL_DATA_PROCESS_TERMS;

    readonly invalidDirectives = viewChildren(InvalidDirective);
    public removeAgbCheckbox$: Observable<boolean>;
    public hideDataProcessTerms$: Observable<boolean>;
    public identicalLoginUiStyle$: Observable<boolean>;
    public backgroundImg$: Observable<any>;
    public brandingData$: Observable<BrandingState>;
    private readonly agbSubscription: Subscription = null;
    private readonly dataProcessTermsSubscription: Subscription = null;

    // BFSG: Focus management for TAN inputs
    private tanInputElements: HTMLInputElement[] = [];
    private focusManagementActive = false;

    constructor(private formBuilder: UntypedFormBuilder,
                private store: Store<AppState>,
                private brandingService: BrandingService,
                private route: ActivatedRoute,
                private accessibilityService: AccessibilityService,
                private translate: TranslateService) {
        this.tanLoginForm = formBuilder.group({
            code1: ['', Validators.compose([Validators.required, Validators.maxLength(1)])],
            code2: ['', Validators.compose([Validators.required, Validators.maxLength(1)])],
            code3: ['', Validators.compose([Validators.required, Validators.maxLength(1)])],
            code4: ['', Validators.compose([Validators.required, Validators.maxLength(1)])],
            code5: ['', Validators.compose([Validators.required, Validators.maxLength(1)])],
            code6: ['', Validators.compose([Validators.required, Validators.maxLength(1)])],
            dataProcessTerm: [null, Validators.compose([Validators.requiredTrue])],
            agbTerm: [null, null]
        }, {validators: [this.dataProcessTermsValidator()]});

        this.brandingData$ = this.brandingService.getBrandingSettingsFromStore().pipe(
            filter(e => e !== null && e !== undefined),
            shareReplay()
        );

        this.removeAgbCheckbox$ = this.brandingData$.pipe(map(e => e.removeAgbCheckbox));
        this.hideDataProcessTerms$ = this.brandingData$.pipe(map(e => e.hideDataProcessTerms));
        this.identicalLoginUiStyle$ = this.brandingData$.pipe(map(b => !!b?.identicalLoginUiStyle));

        this.agbSubscription = this.removeAgbCheckbox$.subscribe(hideCheckbox => {
            if (hideCheckbox) {
                this.tanLoginForm.controls['agbTerm'].setValue(true);
            }
        });

        this.dataProcessTermsSubscription = this.hideDataProcessTerms$.subscribe(hideCheckbox => {
            if (hideCheckbox) {
                this.tanLoginForm.controls['dataProcessTerm'].setValue(true);
            }
        });

        this.backgroundImg$ = this.brandingData$.pipe(
            map(settings => {
                const backgroundImg = settings.bgImage_patientLogin || '/assets/img/general/praxis_bg.jpg';
                return {backgroundImage: backgroundImg};
            }));

        store.select(s => s.login.error).subscribe(httpError => {
            if (httpError) {
                if (httpError.status === 429) {
                    this.tanLoginForm.setErrors({
                        rateLimitReached: true
                    });
                    // BFSG: Announce rate limit error to screen readers
                    this.translate.get('ACCESSIBILITY.SR_TAN_RATE_LIMIT_ERROR').subscribe(message => {
                        this.accessibilityService.announceToScreenReader(message, 'assertive');
                    });
                } else {
                    this.tanLoginForm.setErrors({
                        notValid: true
                    });
                    // BFSG: Announce TAN login error to screen readers
                    this.translate.get('ACCESSIBILITY.SR_TAN_INVALID_ERROR').subscribe(message => {
                        this.accessibilityService.announceToScreenReader(message, 'assertive');
                    });
                }
            }
        });
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(
            take(1),
            filter((params: Params) => params && this.isValidTanFromParams(params)),
            tap((params: Params) => this.setCodeControls(params.tan))
        ).subscribe();

        // BFSG: Set up enhanced focus management for TAN inputs
        setTimeout(() => {
            this.setupTanInputFocusManagement();
        }, 100);
    }

    ngOnDestroy(): void {
        if (this.agbSubscription !== null) {
            this.agbSubscription.unsubscribe();
        }

        if (this.dataProcessTermsSubscription !== null) {
            this.dataProcessTermsSubscription.unsubscribe();
        }

        // BFSG: Clean up focus management
        this.focusManagementActive = false;
    }

    public onSubmit() {
        if (this.tanLoginForm.valid) {
            // BFSG: Announce login attempt to screen readers
            this.translate.get('ACCESSIBILITY.SR_TAN_VERIFYING').subscribe(message => {
                this.accessibilityService.announceToScreenReader(message, 'polite');
            });
            this.login(this.getTan());
        } else {
            Object.keys(this.tanLoginForm.controls).forEach(key => {
                this.tanLoginForm.get(key).markAsDirty();
            });
            this.invalidDirectives().map((directive) => {
                directive.triggerTooltip();
            });
            // BFSG: Announce validation errors to screen readers
            this.translate.get('ACCESSIBILITY.SR_TAN_VALIDATION_ERROR').subscribe(message => {
                this.accessibilityService.announceToScreenReader(message, 'assertive');
            });
        }
    }

    public handleBackspace(event) {
        if (!event.target.value) {
            this.goToPreviousInput(event);
        }
    }

    public handleInput(event) {
        if (event.inputType === 'deleteContentBackward') {
            this.goToPreviousInput(event);
        } else {
            this.goToNextInput(event);
        }

        // BFSG: Update progress when input changes
        if (this.focusManagementActive) {
            setTimeout(() => this.updateTanProgress(), 50);
        }
    }

    public handleFocus(event) {
        const target = event.srcElement || event.target;
        target.setSelectionRange(0, 9999);
    }

    public goToNextInput(event) {
        const target = event.srcElement || event.target;
        if (target.nextElementSibling) {
            setTimeout(() => {
                target.nextElementSibling.focus();
            }, 5);
        }
    }

    public goToPreviousInput(event) {
        const target = event.srcElement || event.target;
        if (target.previousElementSibling) {
            setTimeout(() => {
                target.previousElementSibling.focus();
            }, 5);
        }
    }

    public onPaste(event) {
        const copiedText = event.clipboardData.getData('Text');
        this.fillTANIntoForm(this.prepareTANString(copiedText));
    }

    private login(tan: string): void {
        this.store.dispatch(new DoTanLoginAction(tan));
    }

    private getTan(): string {
        const value = this.tanLoginForm.value;
        return value.code1 + value.code2 + value.code3 + value.code4 + value.code5 + value.code6;
    }

    private fillTANIntoForm(tan: string) {
        for (let i = 1; i <= 6; i++) {
            this.tanLoginForm.controls['code' + i].setValue(tan[i - 1]);
        }
    }

    private prepareTANString(s: string) {
        return s.replace(' ', '').toUpperCase();
    }

    private isValidTanFromParams(params: Params): boolean {
        return params.tan && params.tan.length === 6;
    }

    private getCodeFormControls(): AbstractControl[] {
        return Object.keys(this.tanLoginForm.controls).reduce((controls: AbstractControl[], controlName: string) => {
            if (controlName.startsWith('code')) {
                controls.push(this.tanLoginForm.controls[controlName]);
            }

            return controls;
        }, []);
    }

    private setCodeControls(tan: string): void {
        const codeFormControl: AbstractControl[] = this.getCodeFormControls();

        tan.split('').forEach((code: string, index: number) => codeFormControl[index].setValue(code));
    }

    private dataProcessTermsValidator(): ValidatorFn {
        // Because the validation messages for both dataProcessTerm and agbTerm controls are the same
        // and visually overlap each other when they are active, this custom validation was created.
        return (fg: UntypedFormGroup): ValidationErrors | null => {
            if (fg.controls['dataProcessTerm'].value === true && fg.controls['agbTerm'].value !== true) {
                if (fg.controls['agbTerm'].valid === true) {
                    fg.controls['agbTerm'].setErrors({'incorrect': true});
                }
            } else {
                fg.controls['agbTerm'].setErrors(null);
            }
            return null;
        };
    }

    // BFSG: Enhanced focus management for TAN inputs
    private setupTanInputFocusManagement(): void {
        // Get all TAN input elements
        this.tanInputElements = [
            document.getElementById('code1') as HTMLInputElement,
            document.getElementById('code2') as HTMLInputElement,
            document.getElementById('code3') as HTMLInputElement,
            document.getElementById('code4') as HTMLInputElement,
            document.getElementById('code5') as HTMLInputElement,
            document.getElementById('code6') as HTMLInputElement
        ].filter(el => el !== null);

        if (this.tanInputElements.length === 6) {
            this.focusManagementActive = true;
            this.setupTanInputAccessibility();

            // BFSG: Focus first TAN input initially
            this.accessibilityService.setFocusAfterDelay(this.tanInputElements[0], 300);

            // BFSG: Announce TAN input setup to screen readers
            this.translate.get('ACCESSIBILITY.SR_TAN_INPUT_READY').subscribe(message => {
                this.accessibilityService.announceToScreenReader(message, 'polite');
            });
        }
    }

    private setupTanInputAccessibility(): void {
        this.tanInputElements.forEach((input, index) => {
            // BFSG: Enhanced keyboard navigation
            input.addEventListener('keydown', (event) => {
                this.handleTanInputKeydown(event, index);
            });

            // BFSG: Add ARIA live region updates
            input.addEventListener('input', () => {
                this.updateTanProgress();
            });

            // BFSG: Handle focus events with announcements
            input.addEventListener('focus', () => {
                if (this.focusManagementActive) {
                    this.translate.get('ACCESSIBILITY.SR_TAN_DIGIT_FOCUSED', {
                        position: index + 1,
                        total: 6
                    }).subscribe(message => {
                        this.accessibilityService.announceToScreenReader(message, 'polite');
                    });
                }
            });
        });
    }

    private handleTanInputKeydown(event: KeyboardEvent, currentIndex: number): void {
        const key = event.key;

        // BFSG: Enhanced keyboard navigation
        switch (key) {
            case 'ArrowRight':
                event.preventDefault();
                this.focusNextTanInput(currentIndex);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.focusPreviousTanInput(currentIndex);
                break;
            case 'Home':
                event.preventDefault();
                this.accessibilityService.setFocusAfterDelay(this.tanInputElements[0], 50);
                break;
            case 'End':
                event.preventDefault();
                this.accessibilityService.setFocusAfterDelay(this.tanInputElements[5], 50);
                break;
            case 'Delete':
                // Clear current and move to next
                this.tanInputElements[currentIndex].value = '';
                this.tanLoginForm.get(`code${currentIndex + 1}`)?.setValue('');
                if (currentIndex < 5) {
                    this.focusNextTanInput(currentIndex);
                }
                break;
        }
    }

    private focusNextTanInput(currentIndex: number): void {
        if (currentIndex < 5) {
            this.accessibilityService.setFocusAfterDelay(this.tanInputElements[currentIndex + 1], 50);
        }
    }

    private focusPreviousTanInput(currentIndex: number): void {
        if (currentIndex > 0) {
            this.accessibilityService.setFocusAfterDelay(this.tanInputElements[currentIndex - 1], 50);
        }
    }

    private updateTanProgress(): void {
        if (!this.focusManagementActive) return;

        const filledInputs = this.tanInputElements.filter(input => input.value.length > 0).length;

        // BFSG: Update live region for screen readers
        const liveRegion = document.getElementById('tan-progress-announcer');
        if (liveRegion) {
            this.translate.get('ACCESSIBILITY.SR_TAN_PROGRESS', {
                filled: filledInputs,
                total: 6
            }).subscribe(message => {
                liveRegion.textContent = message;
                // Also use AccessibilityService for fallback
                this.accessibilityService.announceToScreenReader(message, 'polite');
            });
        }

        // BFSG: Auto-focus next empty input
        if (filledInputs < 6) {
            const nextEmptyIndex = this.tanInputElements.findIndex(input => input.value.length === 0);
            if (nextEmptyIndex !== -1) {
                this.accessibilityService.setFocusAfterDelay(this.tanInputElements[nextEmptyIndex], 100);
            }
        }
    }
}
